<!-- 
Dialog for testing SAP connectivity.
-->
<template>
  <v-dialog :value="show" persistent content-class="dialog md">
    <v-form ref="form">
      <v-card>
        <v-toolbar color="secondary" flat dark>
          <v-toolbar-title>
            Test SAP connectivity
          </v-toolbar-title>
          <v-spacer/>
          <our-user-guide-button topic="administration-exception-logs" section="diagnostic-tools-sap-connectivity"/>
        </v-toolbar>
        <our-busy-indicator :show="busy"/>
        <v-card-text>
          <v-container grid-list-md pa-0>
            <v-layout wrap>
              <v-flex xs12 md8>
                <provider-autocomplete @input="providerSelected"/>
              </v-flex>
              <v-flex xs12 md4>
                <v-select
                  v-model="levyCollectionType"
                  :rules="[rules.required]"
                  :items="levyCollectionTypes"
                  label="Levy collection type*" 
                  @keydown.enter.native.prevent
                />
              </v-flex>
              <v-flex xs12>
                <v-alert :value="readingWasSuccessful" type="success">
                  <template v-if="readingFoundNoMatches">
                    <p>Reading from SAP was successful - no matching customer records were found.</p>
                    <p v-if="hasContactForSapAccount">If you want to test that LTPS can write to SAP, the system will create a customer record for the specified provider.</p>
                    <p v-else>The selected provider does not have a primary contact suitable for creating a SAP account.</p>
                  </template>
                  <template v-else>
                    <p class="mb-0">Reading from SAP was successful. The following matching customer records were found:</p>
                    <ul>
                      <li v-for="(match, i) in sapCustomerAccounts" :key="`read-match-${i}`">
                        CustomerId={{ match.customerId }}; Name1={{ match.name1 }}; SearchTerm={{ match.searchTerm }}
                      </li>
                    </ul>
                    <p class="mt-2">If you want to test that LTPS can write to SAP you will need to choose a provider that does not yet have a customer record.</p>
                  </template>
                </v-alert>
                <v-alert :value="readingFailed" type="warning">
                  SAP connectivity test failed. See error logs for more detail.
                </v-alert>
              </v-flex>
              <v-flex xs12>
                <v-alert :value="writingWasSuccessful" type="success">
                  <p>Writing to SAP was successful. A customer record was created with id {{ stateForWriting.data }}.</p>
                </v-alert>
                <v-alert :value="writingFailed" type="warning">
                  SAP connectivity test failed. See error logs for more detail.
                </v-alert>
              </v-flex>
            </v-layout>
          </v-container>
          <our-required-field-indicator/>
        </v-card-text>
        <v-card-actions>
          <v-spacer/>
          <v-btn :disabled="busy" flat @click="hide">Close</v-btn>
          <v-btn :disabled="!canTestWriting" flat color="primary" @click="testWritingToSap">
            Test (Write)
          </v-btn>
          <v-btn :disabled="!canTestReading" flat color="primary" @click="testReadingFromSap">
            Test (Read)
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
    <our-validation-dialog v-model="showValidationDialog" :form="this.$refs.form" />
  </v-dialog>
</template>

<script>
import ProviderAutocomplete from "@/components/providers/ProviderAutocomplete.vue";
import * as action from "@/store/providers/action-types";
import * as mutation from "@/store/providers/mutation-types";
import * as exceptionLogAction from "@/store/exception-logs/action-types";
import ruleMixins from "@/mixins/rule.mixins";
import lookupMixins from "@/mixins/lookup.mixins";
import { GLOBAL_ERROR_HIDE } from "@/store/system/mutation-types";

export default {
  components: { ProviderAutocomplete },
  mixins: [ruleMixins, lookupMixins],
  model: {
    prop: "show",
    event: "update:show"
  },
  props: {
    show: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      showValidationDialog: false,
      providerId: null,
      levyCollectionType: null
    };
  },
  computed: {
    stateForReading() {
      return this.$store.state.providers.sapTestReading;
    },
    stateForWriting() {
      return this.$store.state.providers.sapTestWriting;
    },
    sapCustomerAccounts() {
      return this.stateForReading.data.sapCustomerAccounts;
    },
    hasContactForSapAccount() {
      return this.stateForReading.data.hasContactForSapAccount;
    },
    busy() {
      return this.stateForReading.loading || this.stateForWriting.saving;
    },
    readingFailed() {
      return this.stateForReading.successful === false;
    },
    readingWasSuccessful() {
      return this.stateForReading.successful === true;
    },
    readingFoundNoMatches() {
      return this.readingWasSuccessful && this.sapCustomerAccounts.length == 0;
    },
    writingFailed() {
      return this.stateForWriting.successful === false;
    },
    writingWasSuccessful() {
      return this.stateForWriting.successful === true;
    },
    levyCollectionTypes() {
      return this.getLookupsWithoutAttribute(this.lookups.levyCollectionTypes, "IsSandbox");
    },
    canTestReading() {
      return !this.busy && !this.readingWasSuccessful;
    },
    canTestWriting() {
      return !this.busy && this.readingFoundNoMatches && this.hasContactForSapAccount && !this.writingWasSuccessful;
    }
  },
  watch: {
    show(nowShowing) {
      if (nowShowing) {
        this.$refs.form.reset();
        this.$store.commit(mutation.SAP_TEST_BLANK);
      }
    },
    providerId() {
      this.$store.commit(mutation.SAP_TEST_BLANK);
    },
    levyCollectionType() {
      this.$store.commit(mutation.SAP_TEST_BLANK);
    }
  },
  methods: {
    hide() {
      this.$store.commit(GLOBAL_ERROR_HIDE);
      if (this.$store.state.exceptionLogs.listing.loaded) {
        this.$store.dispatch(exceptionLogAction.LIST);
      }
      this.$emit("update:show", false);
    },
    providerSelected(provider) {
      this.providerId = provider ? provider.id : null;
    },
    async testReadingFromSap() {
      this.showValidationDialog = !this.$refs.form.validate();
      if (!this.showValidationDialog) {
        await this.$store.dispatch(action.SAP_TEST_READING, {
          providerId: this.providerId,
          levyCollectionType: this.levyCollectionType
        });
      }
    },
    async testWritingToSap() {
      this.showValidationDialog = !this.$refs.form.validate();
      if (!this.showValidationDialog) {
        await this.$store.dispatch(action.SAP_TEST_WRITING, {
          providerId: this.providerId,
          levyCollectionType: this.levyCollectionType
        });
      }
    }
  }
};
</script>
